import * as React from 'react';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faAngleDown,
    faAngleUp,
    faArrowDownToLine,
    faClockRotateLeft,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';

import '../Main.scss';

import { FinancialWorkbook, PortalTextSubmissionDetails } from '../../Models/Api/strongbox.financialportal';
import { DownloadableFile, WorkbookToDownloadableFile } from '../../Utils/FileUtils';
import { StringFormat } from '../../Utils/StringUtils';

import { FieldguideMark } from './FieldguideMark';

export const SECTION_TAG_PREFIX = 'section:';

export type WorkbookTypeLabel = { label: string; tip: string };

// The type of a workbook with no `section:` tag.
const getDefaultType = (text?: PortalTextSubmissionDetails): WorkbookTypeLabel => ({
    label: text?.workbooksTypeDefault || 'Default',
    tip: text?.workbooksTypeDefaultTip || 'Default workbook: one of the standard Data Extraction outputs generated for every collection.',
});

// The only place a section code is named: maps the value of a `section:` tag to the chip / Type label and tooltip,
// both of which come from the portal text (English until it is fetched).
const SECTION_TYPES: { [code: string]: (text?: PortalTextSubmissionDetails) => WorkbookTypeLabel } = {
    fieldguide: text => ({
        label: text?.workbooksTypeAudit || 'Audit',
        tip: text?.workbooksTypeAuditTip || 'Audit area workbook: generated because Fieldguide is enabled, and scoped to one audit area so it can go to its own request.',
    }),
};

const ALL_FILTER = 'all';
const DEFAULT_FILTER = 'default';

const compareOrdinal = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/** The key two section codes are compared by: section codes are case-insensitive. */
export const sectionKey = (code: string | undefined): string | undefined => code?.toUpperCase();

/**
 * The section of a workbook, by the same rule as the server (FinancialWorkbookOrdering.GetSectionCode in the Core
 * project, which sorts the API response): the value after a `section:` tag (the prefix is case-sensitive), trimmed,
 * ignoring blank values; when there are several, the alphabetically first (ordinal, ignoring case, then ordinal).
 * Returns undefined when the workbook has no section. Keep the two in step; both are tested against the same examples.
 */
export const getSectionCode = (tags: string[] | undefined): string | undefined => {
    const codes = (tags || [])
        .filter(t => !!t && t.startsWith(SECTION_TAG_PREFIX))
        .map(t => t.substring(SECTION_TAG_PREFIX.length).trim())
        .filter(code => code.length > 0)
        .sort((a, b) => compareOrdinal(a.toUpperCase(), b.toUpperCase()) || compareOrdinal(a, b));
    return codes[0];
};

/** Distinct section codes (compared ignoring case) found on the workbooks, in first-seen order. */
export const getSectionCodes = (workbooks: (FinancialWorkbook | undefined)[] | undefined): string[] => {
    const codes: string[] = [];
    (workbooks || []).forEach(wb => {
        const code = wb ? getSectionCode(wb.tags) : undefined;
        if (code !== undefined && !codes.some(c => sectionKey(c) === sectionKey(code))) {
            codes.push(code);
        }
    });
    return codes;
};

const getTypeLabel = (code: string | undefined, text?: PortalTextSubmissionDetails): WorkbookTypeLabel =>
    code === undefined ? getDefaultType(text) : (SECTION_TYPES[code.toLowerCase()]?.(text) || { label: code, tip: '' });

export type WorkbookRow = {
    variantId: string;
    revisionNumber: number;
    filename: string;
    fileSize?: number;
};

/** What the card exposes to the (future) wizard: the selected revision and exactly the checked rows. */
export type WorkbooksSelection = {
    revisionNumber: number | undefined;
    checkedRows: WorkbookRow[];
};

// "Send to Fieldguide" (field-guide-q3-2026) is absent entirely, not just disabled, when the flag is off.
// With the flag on, the click handler is required, so the button can never be a silent no-op.
type FieldGuideProps =
    | { fieldGuideEnabled?: false; onSendToFieldguide?: () => void }
    | { fieldGuideEnabled: true; onSendToFieldguide: () => void };

type Props = FieldGuideProps & {
    workbooks: (FinancialWorkbook | undefined)[];
    workspaceId: string;
    financialRecordId: string | undefined;
    financialRecordCreated: string | undefined;
    onDownload: (files: DownloadableFile[]) => void;
    onSelectionChange?: (selection: WorkbooksSelection) => void;
    text?: PortalTextSubmissionDetails;
};

const rowKey = (variantId: string | undefined, revisionNumber: number | undefined): string =>
    `${variantId}|${revisionNumber}`;

export const WorkbooksCard: React.FC<Props> = (props): React.ReactElement | null => {
    const { workbooks, onSelectionChange, text } = props;
    const allWorkbooks = React.useMemo(
        () => workbooks.filter((wb): wb is FinancialWorkbook => !!wb),
        [workbooks]
    );

    // Revisions that have at least one workbook, newest first.
    const revisions = React.useMemo(() => {
        const result: { number: number; creationTime: string }[] = [];
        allWorkbooks.forEach(wb => {
            if (!result.some(r => r.number === wb.revisionNumber)) {
                result.push({ number: wb.revisionNumber, creationTime: wb.creationTime });
            }
        });
        return result.sort((a, b) => b.number - a.number);
    }, [allWorkbooks]);

    const [selectedRevision, setSelectedRevision] = React.useState<number | undefined>(undefined);
    const [filter, setFilter] = React.useState<string>(ALL_FILTER);
    const [checked, setChecked] = React.useState<Set<string>>(new Set());
    const [menuOpen, setMenuOpen] = React.useState<boolean>(false);
    const revisionSelectRef = React.useRef<HTMLDivElement>(null);
    const selectAllRef = React.useRef<HTMLInputElement>(null);

    // Whether the sticky toolbar is currently pinned below the app header. Only then does it fill the gap above
    // itself and draw its own top edge; at rest the card's border shows normally. Watches a zero-height marker
    // above the card against the toolbar's own sticky offset (read from its computed `top`).
    const [toolbarStuck, setToolbarStuck] = React.useState<boolean>(false);
    const stickSentinelRef = React.useRef<HTMLDivElement>(null);
    const hasRows = revisions.length > 0;

    const currentRevision = revisions.some(r => r.number === selectedRevision)
        ? selectedRevision
        : revisions[0]?.number;

    // Rows of the selected revision, in the order the API returned them.
    const revisionRows = React.useMemo(
        () => allWorkbooks.filter(wb => wb.revisionNumber === currentRevision),
        [allWorkbooks, currentRevision]
    );

    const chipCodes = React.useMemo(() => getSectionCodes(revisionRows), [revisionRows]);
    const chips = [
        { id: ALL_FILTER, label: text?.workbooksFilterAll || 'All', count: revisionRows.length },
        {
            id: DEFAULT_FILTER,
            label: getDefaultType(text).label,
            count: revisionRows.filter(wb => getSectionCode(wb.tags) === undefined).length,
        },
        ...chipCodes.map(code => ({
            id: `${SECTION_TAG_PREFIX}${sectionKey(code)}`,
            label: getTypeLabel(code, text).label,
            count: revisionRows.filter(wb => sectionKey(getSectionCode(wb.tags)) === sectionKey(code)).length,
        })),
    ];
    const activeFilter = chips.some(c => c.id === filter) ? filter : ALL_FILTER;

    const shownRows = React.useMemo(
        () => revisionRows.filter(wb => {
            const code = getSectionCode(wb.tags);
            return activeFilter === ALL_FILTER
                || (activeFilter === DEFAULT_FILTER ? code === undefined : activeFilter === `${SECTION_TAG_PREFIX}${sectionKey(code)}`);
        }),
        [revisionRows, activeFilter],
    );

    // `checked` holds the user's own picks and a filter never prunes it, so rows a filter hides keep their
    // check and come back checked when the filter widens. Download, the counts and the wizard callback act
    // on the intersection of those picks and the rows shown.
    const checkedWorkbooks = React.useMemo(
        () => shownRows.filter(wb => checked.has(rowKey(wb.variantId, wb.revisionNumber))),
        [shownRows, checked],
    );
    const shownChecked = checkedWorkbooks.length;
    const allShownChecked = shownRows.length > 0 && shownChecked === shownRows.length;

    // `indeterminate` is DOM-only (no React prop), so set it on the element.
    React.useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = shownChecked > 0 && !allShownChecked;
        }
    }, [shownChecked, allShownChecked]);

    React.useEffect(() => {
        if (!menuOpen) {
            return;
        }
        const onPointerDown = (e: MouseEvent): void => {
            if (!revisionSelectRef.current?.contains(e.target as Node)) {
                setMenuOpen(false);
            }
        };
        const onKeyDown = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [menuOpen]);

    React.useEffect(() => {
        const sentinel = stickSentinelRef.current;
        const toolbar = sentinel?.parentElement?.querySelector('.page-workbook-toolbar');
        if (!sentinel || !toolbar || typeof IntersectionObserver === 'undefined') {
            return;
        }
        const offset = parseFloat(getComputedStyle(toolbar).top) || 0;
        const observer = new IntersectionObserver(
            ([entry]) => setToolbarStuck(!entry.isIntersecting && entry.boundingClientRect.top < offset),
            { rootMargin: `-${offset}px 0px 0px 0px`, threshold: 0 },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasRows]);

    // Always call the latest callback without a new callback identity re-firing the effect.
    const onSelectionChangeRef = React.useRef(onSelectionChange);
    React.useEffect(() => {
        onSelectionChangeRef.current = onSelectionChange;
    });

    // Fires on mount and when the revision, the checked set or the filter changes what is selected and shown.
    React.useEffect(() => {
        const checkedRows = checkedWorkbooks.map(wb => ({
            variantId: wb.variantId,
            revisionNumber: wb.revisionNumber,
            filename: wb.filename,
            fileSize: wb.fileSize,
        }));
        onSelectionChangeRef.current?.({ revisionNumber: currentRevision, checkedRows });
    }, [currentRevision, checkedWorkbooks]);

    if (revisions.length === 0) {
        return null;
    }

    const toggle = (wb: FinancialWorkbook): void => {
        const key = rowKey(wb.variantId, wb.revisionNumber);
        const next = new Set(checked);
        if (next.has(key)) {
            next.delete(key);
        } else {
            next.add(key);
        }
        setChecked(next);
    };

    const toggleAllShown = (): void => {
        const next = new Set(checked);
        shownRows.forEach(wb => {
            const key = rowKey(wb.variantId, wb.revisionNumber);
            if (allShownChecked) {
                next.delete(key);
            } else {
                next.add(key);
            }
        });
        setChecked(next);
    };

    const download = (): void => {
        const files = checkedWorkbooks
            .map(wb => WorkbookToDownloadableFile(props.workspaceId, props.financialRecordId, props.financialRecordCreated, wb))
            .filter((f): f is DownloadableFile => !!f);
        props.onDownload(files);
    };

    const anyChecked = checkedWorkbooks.length > 0;

    return (
        <div className={'page-section'}>
            <div className={'page-section__heading'}>
                <div className={'page-revision-bar'}>
                    <h2 id={'workbooks-card-heading'} className={'page-card-header-row__title'}>{text?.workbooksTitle || 'Workbooks'}</h2>
                    <div className={'page-revision-select'} ref={revisionSelectRef}>
                        <button
                            type={'button'}
                            className={'cr-btn cr-btn--floating'}
                            aria-haspopup={'menu'}
                            aria-expanded={menuOpen}
                            onClick={() => setMenuOpen(open => !open)}
                        >
                            <span className={'cr-btn__icon'}><FontAwesomeIcon icon={faClockRotateLeft} /></span>
                            <span className={'cr-btn__label'}>{StringFormat(text?.templateRevision || 'Revision {0}', currentRevision)}</span>
                            <span className={'cr-btn__icon'}><FontAwesomeIcon icon={menuOpen ? faAngleUp : faAngleDown} /></span>
                        </button>
                        {menuOpen && (
                            <div className={'cr-menu page-revision-select__menu'} role={'menu'}>
                                {revisions.map((r, index) => (
                                    <button
                                        key={r.number}
                                        type={'button'}
                                        role={'menuitemradio'}
                                        aria-checked={r.number === currentRevision}
                                        className={'cr-menu-item'}
                                        onClick={() => {
                                            setSelectedRevision(r.number);
                                            setChecked(new Set());
                                            setFilter(ALL_FILTER);
                                            setMenuOpen(false);
                                        }}
                                    >
                                        <input
                                            type={'radio'}
                                            className={'cr-radio'}
                                            checked={r.number === currentRevision}
                                            readOnly
                                            tabIndex={-1}
                                            aria-hidden={'true'}
                                        />
                                        <span className={'cr-menu-item__label page-revision-select__item'}>
                                            <span className={'page-revision-select__name'}>
                                                {StringFormat(text?.templateRevision || 'Revision {0}', r.number)}
                                                {index === 0 && <span className={'cr-tag cr-tag--important'}>{text?.revisionLatest || 'Latest'}</span>}
                                            </span>
                                            <span className={'page-help-text'}>{dayjs(r.creationTime).format('MMMM D, YYYY')}</span>
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div ref={stickSentinelRef} className={'page-stick-sentinel'} aria-hidden={'true'} />
            <div className={'cr-card page-section__body'} role={'region'} aria-labelledby={'workbooks-card-heading'}>
                <div className={`page-workbook-toolbar${toolbarStuck ? ' page-workbook-toolbar--stuck' : ''}`}>
                    <div className={'page-filter-chips'} role={'group'} aria-label={text?.workbooksFilterLabel || 'Filter workbooks'}>
                        {chips.map(chip => {
                            const active = activeFilter === chip.id;
                            return (
                                <button
                                    key={chip.id}
                                    type={'button'}
                                    aria-pressed={active}
                                    className={`cr-btn cr-btn--small ${active ? 'cr-btn--important' : 'cr-btn--secondary'}`}
                                    onClick={() => setFilter(chip.id)}
                                >
                                    <span className={'cr-btn__label'}>
                                        {chip.label} <span className={'page-filter-chips__count'}>{`(${chip.count})`}</span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    {/* Until rows are checked, a hint stands in for the actions. */}
                    <span className={`page-help-text page-workbook-toolbar__hint${anyChecked ? ' page-hidden' : ''}`}>
                        {props.fieldGuideEnabled
                            ? 'Select workbooks to download or send to Fieldguide'
                            : text?.workbooksSelectHint || 'Select workbooks to download.'}
                    </span>
                    <div className={`page-workbook-toolbar__actions${anyChecked ? '' : ' page-hidden'}`}>
                        <button
                            type={'button'}
                            className={'cr-btn cr-btn--secondary'}
                            disabled={!anyChecked}
                            onClick={download}
                        >
                            <span className={'cr-btn__icon'}><FontAwesomeIcon icon={faArrowDownToLine} /></span>
                            <span className={'cr-btn__label'}>
                                {text?.downloadFilesPrompt || 'Download Files'} <span className={'page-filter-chips__count'}>{`(${checkedWorkbooks.length})`}</span>
                            </span>
                        </button>
                        {props.fieldGuideEnabled && (
                            <button
                                type={'button'}
                                className={'cr-btn cr-btn--primary'}
                                disabled={!anyChecked}
                                onClick={() => props.onSendToFieldguide()}
                            >
                                <span className={'cr-btn__icon'}><FieldguideMark /></span>
                                <span className={'cr-btn__label'}>
                                    {text?.workbooksSendToFieldguide || 'Send to Fieldguide'} <span className={'page-filter-chips__count'}>{`(${checkedWorkbooks.length})`}</span>
                                </span>
                            </button>
                        )}
                    </div>
                </div>
                <div className={'cr-table-cell cr-table-cell--column-header page-file-list-header'}>
                    <span className={'cr-table-cell__spacer'} />
                    <span className={'cr-table-cell__slot page-file-row'}>
                        <input
                            ref={selectAllRef}
                            type={'checkbox'}
                            className={'cr-checkbox'}
                            checked={allShownChecked}
                            onChange={toggleAllShown}
                            aria-label={text?.workbooksSelectAll || 'Select all workbooks'}
                        />
                        <span className={'page-file-row__name cr-table-cell__header-text'}>{text?.columnWorkbook || 'Workbook'}</span>
                        <span className={'page-file-row__type cr-table-cell__header-text'}>{text?.columnType || 'Type'}</span>
                        <span className={'page-file-row__size cr-table-cell__header-text'}>{text?.columnSize || 'Size'}</span>
                    </span>
                    <span className={'cr-table-cell__spacer'} />
                </div>
                <div className={'page-file-list page-file-list--under-header'}>
                    {shownRows.map(wb => {
                        const key = rowKey(wb.variantId, wb.revisionNumber);
                        const type = getTypeLabel(getSectionCode(wb.tags), text);
                        return (
                            <div key={key} className={'cr-table-cell'}>
                                <span className={'cr-table-cell__spacer'} />
                                <span
                                    className={'cr-table-cell__slot page-file-row page-file-row--selectable page-file-row--workbook'}
                                    onClick={(e) => {
                                        // The whole row toggles its checkbox, except for the row's own controls.
                                        if (!(e.target as Element).closest('button, a, input, label')) {
                                            toggle(wb);
                                        }
                                    }}
                                >
                                    <input
                                        type={'checkbox'}
                                        className={'cr-checkbox'}
                                        id={`workbooks-card-${key}`}
                                        aria-label={wb.filename}
                                        checked={checked.has(key)}
                                        onChange={() => toggle(wb)}
                                    />
                                    <span className={'page-file-row__name'}>{wb.filename}</span>
                                    <span className={'page-file-row__type'}>
                                        <span
                                            className={'cr-tag cr-tag--secondary'}
                                            aria-label={type.tip ? `${type.label}: ${type.tip}` : type.label}
                                        >
                                            {type.label}
                                        </span>
                                    </span>
                                    <span className={'page-file-row__size'}>
                                        {wb.fileSize ? (wb.fileSize / 1024).toLocaleString(undefined, { maximumFractionDigits: 0 }) : '-'}
                                        {wb.fileSize ? (<React.Fragment>{' '}<span className={'page-file-row__unit'}>{text?.sizeUnitKB || 'KB'}</span></React.Fragment>) : null}
                                    </span>
                                </span>
                                <span className={'cr-table-cell__spacer'} />
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
